/**
 * JobsDashboardPage — /jobs/dashboard
 *
 * One page, two tabs:
 *   • Employer — jobs I posted (or my business posted) + inbox of
 *     applications with status controls.
 *   • Applicant — saved jobs + my applications with status badges.
 *
 * A user can be both simultaneously; we simply show both tabs.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { labelFor } from "@/data/jobCategories";
import {
  Briefcase, Users, Loader2, ChevronDown, ChevronRight, MapPin, Building2,
  Bookmark, Send, CheckCircle2, XCircle, Clock3, Star, Trash2, Flag,
} from "lucide-react";

const STATUS_COLORS = {
  submitted:     "#6B7280",
  shortlisted:   "#F58220",
  interviewing:  "#0E7C86",
  hired:         "#16a34a",
  rejected:      "#DC2626",
  withdrawn:     "#94A3B8",
};

const STATUS_ORDER = ["submitted","shortlisted","interviewing","hired","rejected","withdrawn"];

function StatusPill({ status }) {
  return (
    <span
      className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full"
      style={{ background: `${STATUS_COLORS[status] || "#94A3B8"}20`, color: STATUS_COLORS[status] || "#6B7280" }}
      data-testid={`status-pill-${status}`}
    >
      {status}
    </span>
  );
}

export default function JobsDashboardPage() {
  const { user } = useAuth();
  const [side, setSide] = useState("employer"); // employer | applicant

  if (!user) return (
    <div className="max-w-md mx-auto px-4 py-16 text-center" data-testid="dashboard-signin">
      <Briefcase className="w-10 h-10 mx-auto opacity-40 mb-2" />
      <h1 className="font-display text-2xl mb-2">Sign in to see your dashboard</h1>
      <Link to="/login?redirect=/jobs/dashboard" className="wofy-btn-primary inline-flex">Sign in</Link>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="jobs-dashboard-page">
      <h1 className="font-display text-4xl mb-1">Jobs dashboard</h1>
      <p className="opacity-70 text-sm mb-4">Everything you post and every job you save — one place.</p>

      <div className="border-b mb-6 flex gap-1" style={{ borderColor: "var(--wofy-border)" }}>
        {[
          { id: "employer",  label: "As employer",  Icon: Briefcase },
          { id: "applicant", label: "As applicant", Icon: Users },
        ].map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setSide(id)}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border-b-2"
            style={{
              borderColor: side === id ? "var(--wofy-primary)" : "transparent",
              color: side === id ? "var(--wofy-primary)" : "var(--wofy-text-soft)",
              fontWeight: side === id ? 600 : 400,
            }}
            data-testid={`dashboard-tab-${id}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {side === "employer"  && <EmployerSection user={user} />}
      {side === "applicant" && <ApplicantSection user={user} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Employer
// ---------------------------------------------------------------------------
function EmployerSection({ user }) {
  const [myJobs, setMyJobs] = useState(null);
  const [expanded, setExpanded] = useState({});     // jobId -> true
  const [applications, setApplications] = useState({}); // jobId -> [apps]

  const load = useCallback(async () => {
    // "My posted jobs" = /api/jobs?posted_by_me equivalent. Since we don't
    // have that endpoint, fetch a decent chunk and filter client-side.
    // (Simple + accurate for MVP; add ?mine=1 later if useful.)
    const { data } = await http.get("/jobs", { params: { limit: 100 } });
    setMyJobs((data.items || []).filter((j) => j.posted_by === user.id));
  }, [user.id]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (jobId) => {
    const next = !expanded[jobId];
    setExpanded({ ...expanded, [jobId]: next });
    if (next && !applications[jobId]) {
      try {
        const { data } = await http.get(`/jobs/${jobId}/applications`);
        setApplications((a) => ({ ...a, [jobId]: data.items || [] }));
      } catch (e) { toast.error(e?.response?.data?.detail || "Could not load applications."); }
    }
  };

  const setStatus = async (jobId, appId, status) => {
    try {
      await http.patch(`/applications/${appId}`, { status });
      setApplications((a) => ({
        ...a,
        [jobId]: (a[jobId] || []).map((x) => x.id === appId ? { ...x, status } : x),
      }));
      toast.success(`Marked ${status}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };

  const close = async (jobId) => {
    if (!confirm("Close this job? It won't be visible to new applicants.")) return;
    try {
      await http.patch(`/jobs/${jobId}`, { status: "closed" });
      toast.success("Job closed.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Close failed."); }
  };

  const del = async (jobId) => {
    if (!confirm("Delete this job permanently? Applications stay in your records but the listing is removed.")) return;
    try { await http.delete(`/jobs/${jobId}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  if (myJobs === null) return <div className="py-8 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin opacity-60" /></div>;

  if (myJobs.length === 0) return (
    <div className="wofy-card !p-8 text-center" data-testid="employer-empty">
      <Briefcase className="w-10 h-10 mx-auto opacity-40 mb-2" />
      <p className="font-display text-xl mb-1">You haven't posted a job yet</p>
      <p className="text-sm opacity-70 mb-4">Post one and every applicant will show up here.</p>
      <Link to="/jobs/post" className="wofy-btn-primary inline-flex items-center gap-1.5"><Send className="w-4 h-4" /> Post a job</Link>
    </div>
  );

  return (
    <ul className="space-y-3" data-testid="employer-jobs">
      {myJobs.map((j) => (
        <li key={j.id} className="wofy-card !p-4" data-testid={`employer-job-${j.id}`}>
          <div className="flex items-center gap-3">
            <button onClick={() => toggle(j.id)} className="opacity-60 hover:opacity-100" data-testid={`toggle-${j.id}`}>
              {expanded[j.id] ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </button>
            <div className="min-w-0 flex-1">
              <Link to={`/jobs/${j.slug || j.id}`} className="font-display text-lg leading-tight hover:underline">{j.title}</Link>
              <div className="text-xs opacity-70 mt-0.5">
                {labelFor(j.employment_type)} · {j.remote ? "Remote" : [j.city, j.country].filter(Boolean).join(", ")} · {j.application_count || 0} application{j.application_count === 1 ? "" : "s"}
              </div>
            </div>
            <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: j.status === "active" ? "#DCFCE7" : "#F3F4F6", color: j.status === "active" ? "#16a34a" : "#6B7280" }}>
              {j.status}
            </span>
            {j.status === "active" && (
              <button onClick={() => close(j.id)} className="text-xs underline opacity-70" data-testid={`close-${j.id}`}>Close</button>
            )}
            <button onClick={() => del(j.id)} className="text-red-600 hover:opacity-80" title="Delete" data-testid={`delete-${j.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>

          {expanded[j.id] && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--wofy-border)" }}>
              {!applications[j.id] ? <div className="text-center py-4"><Loader2 className="w-5 h-5 mx-auto animate-spin opacity-60" /></div>
              : applications[j.id].length === 0
                ? <p className="text-sm opacity-70 text-center py-4">No applications yet.</p>
                : (
                  <ul className="space-y-2" data-testid={`applications-${j.id}`}>
                    {applications[j.id].map((app) => (
                      <li key={app.id} className="rounded-lg border p-3" style={{ borderColor: "var(--wofy-border)" }} data-testid={`application-${app.id}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full grid place-items-center text-white font-medium text-sm" style={{ background: "var(--wofy-primary)" }}>
                            {app.applicant_photo ? <img src={app.applicant_photo} alt="" className="w-9 h-9 rounded-full object-cover" />
                                                 : (app.applicant_name || "?")[0]?.toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium truncate">{app.applicant_name || "Applicant"}</div>
                            <div className="text-[11px] opacity-70">{app.applicant_email || "—"} · {new Date(app.created_at).toLocaleDateString()}</div>
                          </div>
                          <StatusPill status={app.status} />
                        </div>
                        {app.cover_note && <p className="text-xs mt-2 opacity-90 whitespace-pre-line line-clamp-4">{app.cover_note}</p>}
                        {(app.resume_url || app.portfolio_links?.length) && (
                          <div className="text-[11px] mt-2 flex flex-wrap gap-3">
                            {app.resume_url    && <a href={app.resume_url} target="_blank" rel="noreferrer" className="underline opacity-90">📄 Resume</a>}
                            {(app.portfolio_links || []).filter(Boolean).map((l, i) => <a key={i} href={l} target="_blank" rel="noreferrer" className="underline opacity-90">🔗 Portfolio {i+1}</a>)}
                          </div>
                        )}
                        <div className="mt-2 flex flex-wrap items-center gap-1.5">
                          {STATUS_ORDER.filter((s) => s !== "withdrawn" && s !== app.status).map((s) => (
                            <button key={s} onClick={() => setStatus(j.id, app.id, s)} className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full border hover:bg-black/5" style={{ borderColor: "var(--wofy-border)" }} data-testid={`set-${app.id}-${s}`}>
                              → {s}
                            </button>
                          ))}
                          <ReportButton kind="applicant" targetId={app.id} label="Report" />
                        </div>
                      </li>
                    ))}
                  </ul>
                )
              }
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// Applicant
// ---------------------------------------------------------------------------
function ApplicantSection({ user }) {
  const [applied, setApplied] = useState(null);
  const [saved, setSaved]     = useState(null);

  useEffect(() => {
    (async () => {
      try { const { data } = await http.get("/me/applications"); setApplied(data.items || []); } catch { setApplied([]); }
      try { const { data } = await http.get("/me/saved-jobs");   setSaved(data.items   || []); } catch { setSaved([]); }
    })();
  }, []);

  const withdraw = async (appId) => {
    if (!confirm("Withdraw this application?")) return;
    try {
      await http.patch(`/applications/${appId}`, { status: "withdrawn" });
      setApplied((a) => a.map((x) => x.id === appId ? { ...x, status: "withdrawn" } : x));
    } catch (e) { toast.error(e?.response?.data?.detail || "Withdraw failed."); }
  };

  if (applied === null || saved === null) return <div className="py-8 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin opacity-60" /></div>;

  return (
    <div className="space-y-6" data-testid="applicant-section">
      <section>
        <h2 className="font-display text-xl mb-3">My applications ({applied.length})</h2>
        {applied.length === 0 ? (
          <p className="text-sm opacity-70">You haven't applied to any jobs yet.</p>
        ) : (
          <ul className="space-y-2" data-testid="my-applications-list">
            {applied.map((app) => (
              <li key={app.id} className="wofy-card !p-3 flex items-center gap-3" data-testid={`my-app-${app.id}`}>
                <Briefcase className="w-4 h-4 opacity-60" />
                <div className="min-w-0 flex-1">
                  <Link to={`/jobs/${app.job_slug || app.job_id}`} className="text-sm font-medium hover:underline truncate">{app.job_title}</Link>
                  <div className="text-[11px] opacity-70">Applied {new Date(app.created_at).toLocaleDateString()}</div>
                </div>
                <StatusPill status={app.status} />
                {app.status !== "withdrawn" && app.status !== "hired" && (
                  <button onClick={() => withdraw(app.id)} className="text-xs underline opacity-70" data-testid={`withdraw-${app.id}`}>Withdraw</button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <h2 className="font-display text-xl mb-3">Saved jobs ({saved.length})</h2>
        {saved.length === 0 ? (
          <p className="text-sm opacity-70">No saved jobs yet. Bookmark ones you want to come back to.</p>
        ) : (
          <ul className="space-y-2" data-testid="saved-jobs-list">
            {saved.map((j) => (
              <li key={j.id} className="wofy-card !p-3 flex items-center gap-3" data-testid={`saved-${j.id}`}>
                <Bookmark className="w-4 h-4 opacity-60" />
                <div className="min-w-0 flex-1">
                  <Link to={`/jobs/${j.slug || j.id}`} className="text-sm font-medium hover:underline">{j.title}</Link>
                  <div className="text-[11px] opacity-70">{labelFor(j.employment_type)} · {j.remote ? "Remote" : [j.city, j.country].filter(Boolean).join(", ")}</div>
                </div>
                {j.salary && <span className="text-xs font-medium" style={{ color: "var(--wofy-primary)" }}>{j.salary}</span>}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared: Report button (mini modal)
// ---------------------------------------------------------------------------
function ReportButton({ kind, targetId, label = "Report" }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim()) return toast.error("Please describe the issue.");
    setBusy(true);
    try {
      await http.post("/reports", { kind, target_id: targetId, reason: reason.trim() });
      toast.success("Report submitted — our team will review it.");
      setOpen(false); setReason("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Report failed."); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full text-red-600 border border-red-200 hover:bg-red-50 inline-flex items-center gap-1" data-testid={`report-${kind}-${targetId}`}>
        <Flag className="w-2.5 h-2.5" /> {label}
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4" onClick={() => setOpen(false)}>
          <div className="wofy-card !p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid={`report-modal-${targetId}`}>
            <h3 className="font-display text-xl mb-1">Report {kind}</h3>
            <p className="text-xs opacity-70 mb-3">Tell us what's wrong (spam, discriminatory content, illegal, misleading, etc.) — our team reviews every report.</p>
            <textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} className="wofy-input mb-3" placeholder="What's the issue?" data-testid="report-reason" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="wofy-btn-ghost">Cancel</button>
              <button onClick={submit} disabled={busy} className="wofy-btn-primary inline-flex items-center gap-2" data-testid="report-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flag className="w-4 h-4" />} Submit report
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export { ReportButton };
